import { computed, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MarketingCampaignRun } from '@principle-theorem/principle-core';
import {
  IMarketingCampaignRun,
  MarketingCampaignRunStatus,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, WithRef } from '@principle-theorem/shared';
import { isNil } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';

export interface IMarketingCampaignRunEntryStats {
  campaignRun: WithRef<IMarketingCampaignRun>;
  maxEntryCount?: number;
  totalEntryCount: number;
  previewEntryCount: number;
  approvedEntryCount: number;
  approvedEntryPercent: number;
  remainingEntryCount: number;
  enrolledEntryCount?: number;
}

export class CampaignRunEntryManager {
  private _refresh$ = new Subject<void>();
  campaignRun: Signal<WithRef<IMarketingCampaignRun> | undefined>;
  maxEntryCount: Signal<number | undefined>;
  totalEntryCount: Signal<number>;
  previewEntryCount: Signal<number>;
  approvedEntryCount: Signal<number>;
  remainingEntryCount: Signal<number>;
  enrolledEntryCount: Signal<number | undefined>;
  stats$: Observable<IMarketingCampaignRunEntryStats | undefined>;
  stats: Signal<IMarketingCampaignRunEntryStats | undefined>;

  canStartCampaignRun: Signal<boolean>;
  canRemovePreviewEntries: Signal<boolean>;
  canApprovePreviewEntries: Signal<boolean>;
  canMovePreviewEntries: Signal<boolean>;
  approvedEntryPercent: Signal<number>;
  canUnenrolActiveEntries: Signal<boolean>;

  constructor(public campaignRun$: Observable<WithRef<IMarketingCampaignRun>>) {
    this.stats$ = combineLatest([
      this.campaignRun$,
      this._refresh$.pipe(startWith(undefined)),
    ]).pipe(switchMap(([campaignRun]) => this._getStats(campaignRun)));
    this.stats = toSignal(this.stats$, { initialValue: undefined });

    this.campaignRun = computed(() => this.stats()?.campaignRun);
    this.maxEntryCount = computed(() => this.stats()?.maxEntryCount);
    this.totalEntryCount = computed(() => this.stats()?.totalEntryCount ?? 0);
    this.previewEntryCount = computed(
      () => this.stats()?.previewEntryCount ?? 0
    );
    this.approvedEntryCount = computed(
      () => this.stats()?.approvedEntryCount ?? 0
    );
    this.approvedEntryPercent = computed(
      () => this.stats()?.approvedEntryPercent ?? 0
    );
    this.remainingEntryCount = computed(
      () => this.stats()?.remainingEntryCount ?? 0
    );
    this.enrolledEntryCount = computed(
      () => this.stats()?.enrolledEntryCount
    );

    this.canRemovePreviewEntries = computed(() => {
      const stats = this.stats();
      return (
        !!stats &&
        stats.campaignRun.status !== MarketingCampaignRunStatus.Draft &&
        stats.previewEntryCount > 0
      );
    });
    this.canStartCampaignRun = computed(() => {
      const stats = this.stats();
      return (
        !!stats &&
        stats.campaignRun.status === MarketingCampaignRunStatus.Draft &&
        stats.previewEntryCount > 0
      );
    });
    this.canApprovePreviewEntries = computed(() => {
      const stats = this.stats();
      return (
        !!stats &&
        stats.campaignRun.status !== MarketingCampaignRunStatus.Draft &&
        stats.previewEntryCount > 0 &&
        stats.remainingEntryCount > 0
      );
    });
    this.canMovePreviewEntries = computed(() => {
      const stats = this.stats();
      return (
        !!stats &&
        stats.campaignRun.status !== MarketingCampaignRunStatus.Draft &&
        stats.previewEntryCount > 0 &&
        stats.remainingEntryCount <= 0
      );
    });
    const canUnenrolActiveEntries$ = combineLatest([
      this.campaignRun$,
      this._refresh$.pipe(startWith(undefined)),
    ]).pipe(
      switchMap(async ([campaignRun]) =>
        Firestore.getCount(MarketingCampaignRun.activeEntriesQuery(campaignRun))
      ),
      map((count) => count > 0)
    );
    this.canUnenrolActiveEntries = toSignal(canUnenrolActiveEntries$, {
      initialValue: false,
    });
  }

  refresh(): void {
    this._refresh$.next();
  }

  private async _getStats(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<IMarketingCampaignRunEntryStats> {
    const campaignRef = MarketingCampaignRun.campaignRef(campaignRun);
    const campaign = await Firestore.getDoc(campaignRef);

    const maxEntryCount = campaign?.maximumEntriesPerRun || undefined;
    const totalEntryCount = await Firestore.getCount(
      MarketingCampaignRun.entriesQuery(campaignRun)
    );
    const previewEntryCount = await Firestore.getCount(
      MarketingCampaignRun.previewEntriesQuery(campaignRun)
    );

    const approvedEntryCount = totalEntryCount - previewEntryCount;

    const remainingEntryCount = isNil(maxEntryCount)
      ? previewEntryCount
      : Math.max(0, Math.min(previewEntryCount, maxEntryCount - approvedEntryCount));

    // Total capacity is the number of approved entries plus any remaining
    // slots available for new entries (clamped to zero to avoid negatives
    // when approved entries already exceed the max).
    const totalCapacity = approvedEntryCount + Math.max(remainingEntryCount, 0);
    const approvedEntryPercent =
      totalCapacity > 0 ? (approvedEntryCount / totalCapacity) * 100 : 0;

    const enrolledEntryCount = campaignRun.hasTrackedEnrolledEntries
      ? await MarketingCampaignRun.getEnrolledEntriesCount(campaignRun)
      : undefined;

    return {
      campaignRun,
      maxEntryCount,
      totalEntryCount,
      previewEntryCount,
      approvedEntryCount,
      approvedEntryPercent,
      remainingEntryCount,
      enrolledEntryCount,
    };
  }
}
